import { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import ProgressTimeline from '../ProgressTimeline/ProgressTimeline'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import { EMAIL_REGEX } from '../../helpers/constants'
import { getMinutesofDayFromDate } from '../../helpers/helpers'
import {
  concatTagsWithBuildings,
  splitBuildingsAndTags,
} from '../Organisation/CommunicationPortal/helpers'
import API from '../../api/api'
import MobilePreviewContainer from '../MobilePreviewContainer/MobilePreviewContainer'

import {
  prepareInitSurveyFormValues,
  satisfactionSurvey,
  timeBasedSurvey,
  getId,
  preparePreviewBg,
  ADMIN_OR_PM,
  isCommunicationTypeSms,
  getResidentsFilterOptions,
  FULL_DAY_MINUTES,
  inAppCommunication,
} from './helpers'
import Parameters from './Parameters'
import Advanced from './Advanced'
import FrequencyVisibility from './FrequencyVisibility'
import SurveyModalContainer from './SurveyModalContainer'
import LivePreview from './LivePreview'

const SurveyFormModal = ({
  currentSurvey,
  buildings,
  selectedBuilding,
  onClose,
  onSuccess,
  userRole,
  isDuplicateForm,
}) => {
  const { t } = useTranslation(['surveys'])
  const isEdit = currentSurvey && !isDuplicateForm
  const headerTitle = isEdit ? t('surveys:form.title.edit') : t('surveys:form.title.create')
  const steps = [
    t('surveys:form.stepParameters'),
    t('surveys:form.stepFrequencyVisibility'),
    t('surveys:form.stepAdvanced'),
  ]
  const [currentStep, setCurrentStep] = useState(0)
  const { displaySnackbar } = useSnackbar()

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1)
  }
  const handleBack = () => {
    if (!currentStep) {
      return onClose()
    }
    setCurrentStep((prev) => prev - 1)
  }

  const handleFormSubmit = (values, { setSubmitting }) => {
    const {
      buildings,
      type,
      questions,
      subtype,
      communicationType,
      startDate,
      startTime,
      endDate,
      visibilityPerDay,
      visibilityDays,
      frequency,
      responseTrigger,
      socialLinkService,
      replyTo,
      startTriggerDays,
      startTriggerTime,
      startTriggerType,
      enablePushNotifications,
      residents,
      groups,
      units,
      yardiPropertyId,
      phase,
      buildingNumber,
      floor,
      clientTags,
    } = values

    const dataToSend = {
      visibilityPerDay: +visibilityPerDay,
      visibilityDays: +visibilityDays,
    }

    if (frequency === timeBasedSurvey) {
      dataToSend.startDate = startDate
      dataToSend.startTime =
        getMinutesofDayFromDate(new Date(startTime).getTime()) || FULL_DAY_MINUTES
      dataToSend.endDate = endDate
    }
    const residencyStatuses = []

    const _residents = residents.filter((resident) => {
      if (getResidentsFilterOptions(t).find((option) => option.id === resident.id)) {
        residencyStatuses.push(resident.id)
      }
      return !getResidentsFilterOptions(t).find((option) => option.id === resident.id)
    })

    dataToSend.type = type
    dataToSend.communicationType = communicationType
    dataToSend.isOneTime = frequency === timeBasedSurvey
    dataToSend.responseTrigger = responseTrigger
    dataToSend.socialLinkService = socialLinkService
    dataToSend.enablePushNotifications = enablePushNotifications
    dataToSend.residencyStatuses = residencyStatuses
    dataToSend.clientIds = getId(_residents)
    dataToSend.unitTypes = getId(units)
    dataToSend.communityGroups = getId(groups)
    dataToSend.clientTags = clientTags.map((tag) => tag.text)

    if (responseTrigger) {
      dataToSend.reviewVia = 'email'
      dataToSend.replyTo = replyTo
    }

    if (type === satisfactionSurvey && subtype) {
      dataToSend.subtype = subtype
    }
    const { buildings: _buildings, buildingTags } = splitBuildingsAndTags(buildings)

    const preparedQuestions = questions.map(({ title, answerOptions }) =>
      type === satisfactionSurvey ? { title } : { title, options: answerOptions },
    )

    dataToSend.buildingTags = buildingTags
    dataToSend.buildings = _buildings
    dataToSend.questions = preparedQuestions

    if (frequency !== timeBasedSurvey) {
      dataToSend.startTriggerDays = startTriggerDays
      dataToSend.startTriggerTime = startTriggerTime
      dataToSend.startTriggerType = startTriggerType
    }

    if (yardiPropertyId) {
      dataToSend.yardiPropertyId = yardiPropertyId
      dataToSend.phase = phase
      dataToSend.buildingNumber = buildingNumber
      dataToSend.floor = floor
    }

    const handleSurvey = isEdit ? API.editSurvey : API.createSurvey
    handleSurvey(dataToSend, currentSurvey && currentSurvey.id)
      .then(() => {
        displaySnackbar(isEdit ? t('surveys:table.successEdit') : t('surveys:table.successCreate'))
        onSuccess?.()
        onClose()
      })
      .catch(() => {
        displaySnackbar(
          isEdit ? t('surveys:table.errorEdit') : t('surveys:table.errorCreate'),
          true,
        )
      })
      .finally(() => setSubmitting(false))
  }

  const prefilledBuilding = selectedBuilding ? [selectedBuilding] : []
  const initialValues = prepareInitSurveyFormValues(t, currentSurvey, buildings, prefilledBuilding)

  const listOfBuildingsAndTags = useMemo(
    () => (userRole === 'PropertyManager' ? buildings : concatTagsWithBuildings(buildings)),
    [buildings],
  )

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleFormSubmit}>
      {({ handleChange, setFieldValue, values, handleSubmit, isSubmitting }) => {
        const isAdminOrPM = ADMIN_OR_PM.includes(userRole)

        const isEditingDisabled = isEdit && ['On hold', 'Ongoing'].includes(currentSurvey.status)
        const frequecnyInputsDisabled =
          isEdit && !['On hold', 'Scheduled'].includes(currentSurvey.status)

        const isFirstStep = currentStep === 0
        const isLastStep = currentStep === 2
        const isNextDisabled = () => {
          if (isFirstStep) {
            const { type, buildings, questions, communicationType } = values

            if (isEditingDisabled) {
              return false
            }

            if (!buildings.length || !questions.length) {
              return true
            }

            if (
              isCommunicationTypeSms(values.communicationType) &&
              buildings.some((building) => !building.announcementPhone)
            ) {
              return true
            }

            if (['email', 'sms'].includes(communicationType) && questions.length > 1) {
              return true
            }

            if (type === satisfactionSurvey) {
              return questions.filter((q) => !q.title).length
            } else {
              return questions.filter(
                (q) => !q.title || !q.answerOptions || !q.answerOptions.filter(Boolean).length,
              ).length
            }
          }

          if (currentStep === 1) {
            if (isEditingDisabled) {
              return false
            }

            const {
              startDate,
              endDate,
              startTime,
              visibilityPerDay,
              visibilityDays,
              frequency,
              startTriggerDays,
              residents,
              communicationType,
              units,
              groups,
              clientTags,
            } = values
            const frequencyFields =
              frequency !== timeBasedSurvey
                ? !startTriggerDays
                : !startDate || !endDate || !startTime

            const visibilityFields =
              communicationType === inAppCommunication
                ? !visibilityPerDay || !visibilityDays
                : false

            const resipients =
              !residents.length && !units.length && !groups.length && !clientTags.length

            if (frequencyFields || visibilityFields || resipients) {
              return true
            }
          }

          if (isLastStep && values.responseTrigger) {
            if (!values.socialLinkService) return true
            if (!values.replyTo || !EMAIL_REGEX.test(values.replyTo)) return true
          }
        }

        const renderStep = () => {
          switch (currentStep) {
            case 0:
              return (
                <Parameters
                  userRole={userRole}
                  buildings={listOfBuildingsAndTags}
                  values={values}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  isEdit={isEdit}
                  currentSurvey={currentSurvey}
                  isEditingDisabled={isEditingDisabled}
                />
              )
            case 1:
              return (
                <FrequencyVisibility
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  isEdit={isEdit}
                  isEditingDisabled={isEditingDisabled}
                  frequecnyInputsDisabled={frequecnyInputsDisabled}
                  isAdminOrPM={isAdminOrPM}
                />
              )
            case 2:
              return (
                <Advanced
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  buildingId={selectedBuilding && selectedBuilding.id}
                  currentSurvey={currentSurvey}
                  isEditingDisabled={isEditingDisabled}
                />
              )
          }
        }
        const bg = preparePreviewBg(values.communicationType)
        const selectedBuildingName = selectedBuilding ? selectedBuilding.name : ''

        return (
          <SurveyModalContainer
            headerTitle={headerTitle}
            onClose={onClose}
            modalBody={
              <div className={`survey-form ${isSubmitting ? 'is-loading' : ''}`}>
                <ProgressTimeline currentStep={currentStep} steps={steps} spaceBetweenView />
                {renderStep()}
                <div className="survey-form--buttons">
                  <button className="button button-gray" onClick={handleBack}>
                    {isFirstStep ? t('surveys:form.cancel') : t('surveys:form.back')}
                  </button>
                  <button
                    className="button button-black"
                    type={isLastStep ? 'submit' : 'button'}
                    onClick={isLastStep ? handleSubmit : handleNext}
                    disabled={isSubmitting || isNextDisabled()}
                  >
                    {isLastStep ? t('surveys:form.submit') : t('surveys:form.next')}
                  </button>
                </div>
              </div>
            }
            preview={
              <MobilePreviewContainer
                hideAppBar
                additionalImg={
                  bg ? <img className="live-preview__container--img" src={bg} /> : null
                }
              >
                <LivePreview
                  data={values}
                  buildingName={
                    values.buildings.length ? values.buildings[0].name : selectedBuildingName
                  }
                />
              </MobilePreviewContainer>
            }
          />
        )
      }}
    </Formik>
  )
}

SurveyFormModal.propTypes = {
  currentSurvey: PropTypes.object,
  buildings: PropTypes.array.isRequired,
  selectedBuilding: PropTypes.object,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  userRole: PropTypes.string,
  isDuplicateForm: PropTypes.bool,
}

export default SurveyFormModal
